import React, { useEffect, useState, useRef, useCallback } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import { Card, CardBody, Row, Col, Input, Button } from "reactstrap"
import Map, {
  Marker,
  Source,
  Layer,
  Popup,
  NavigationControl,
  FullscreenControl,
  ScaleControl,
} from "react-map-gl/mapbox"
import "mapbox-gl/dist/mapbox-gl.css"
import { fetchData, FetchPostData } from "utils/Common_api_functions"
import Swal from "sweetalert2"
import Loader from "components/Loader/Loader"
import { FaRoute, FaRoad, FaMoneyBillWave } from "react-icons/fa"
import { MdLocationOn } from "react-icons/md"
import PermittedComponent from "PermittedComponent"
import { usePermissionData } from "AppContext"

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN

const MAP_STYLES = [
  { label: "Streets", value: "mapbox://styles/mapbox/streets-v12" },
  { label: "Outdoors", value: "mapbox://styles/mapbox/outdoors-v12" },
  { label: "Light", value: "mapbox://styles/mapbox/light-v11" },
  { label: "Dark", value: "mapbox://styles/mapbox/dark-v11" },
  { label: "Satellite", value: "mapbox://styles/mapbox/satellite-v9" },
  {
    label: "Satellite Streets",
    value: "mapbox://styles/mapbox/satellite-streets-v12",
  },
  {
    label: "Navigation Day",
    value: "mapbox://styles/mapbox/navigation-day-v1",
  },
  {
    label: "Navigation Night",
    value: "mapbox://styles/mapbox/navigation-night-v1",
  },
]

/* ================= OVERLAP SPREAD HELPER ================= */
const OFFSET_DEG = 0.00012

function spreadOverlappingMarkers(markers) {
  const groups = {}
  markers.forEach(m => {
    const key = `${m.position[0].toFixed(6)},${m.position[1].toFixed(6)}`
    if (!groups[key]) groups[key] = []
    groups[key].push(m)
  })

  const spread = []
  Object.values(groups).forEach(group => {
    if (group.length === 1) {
      spread.push({ ...group[0] })
      return
    }
    const [baseLat, baseLng] = group[0].position
    const total = group.length
    group.forEach((m, i) => {
      const angleDeg = -90 + (360 / total) * i
      const angleRad = (angleDeg * Math.PI) / 180
      const radius = OFFSET_DEG * (total > 2 ? 1.4 : 1)
      spread.push({
        ...m,
        position: [
          baseLat + radius * Math.cos(angleRad),
          baseLng + radius * Math.sin(angleRad),
        ],
        _overlapped: true,
      })
    })
  })
  return spread
}

/* ================= CUSTOM MARKER COMPONENTS ================= */

const CalloutMarker = ({ borderColor, bgColor, iconHtml, labelText, time }) => (
  <div
    style={{
      position: "relative",
      display: "inline-flex",
      flexDirection: "column",
      alignItems: "center",
      cursor: "pointer",
    }}
  >
    <div
      style={{
        background: "#fff",
        border: `2px solid ${borderColor}`,
        borderRadius: 10,
        padding: "5px 10px 5px 6px",
        display: "flex",
        alignItems: "center",
        gap: 7,
        boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
        whiteSpace: "nowrap",
        fontFamily: "sans-serif",
        fontSize: 12,
        fontWeight: 700,
        color: "#1e293b",
      }}
    >
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: bgColor,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 900,
          flexShrink: 0,
        }}
      >
        {iconHtml}
      </span>
      <span
        style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}
      >
        <span>{labelText}</span>
        {time && (
          <span style={{ fontSize: 10, fontWeight: 600, color: "#64748b" }}>
            {time}
          </span>
        )}
      </span>
    </div>
    <div
      style={{
        width: 0,
        height: 0,
        borderLeft: "7px solid transparent",
        borderRight: "7px solid transparent",
        borderTop: `7px solid ${borderColor}`,
      }}
    />
  </div>
)

const LiveMarker = ({ time }) => (
  <div
    style={{
      position: "relative",
      display: "inline-flex",
      flexDirection: "column",
      alignItems: "center",
      cursor: "pointer",
    }}
  >
    <div
      style={{
        background: "#fff",
        border: "2px solid #3b82f6",
        borderRadius: 10,
        padding: "5px 12px 5px 8px",
        display: "flex",
        alignItems: "center",
        gap: 7,
        boxShadow: "0 4px 12px rgba(59,130,246,0.25)",
        whiteSpace: "nowrap",
        fontFamily: "sans-serif",
        fontSize: 12,
        fontWeight: 800,
        color: "#3b82f6",
      }}
    >
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: "#3b82f6",
          boxShadow: "0 0 0 3px rgba(59,130,246,0.25)",
          flexShrink: 0,
          display: "inline-block",
        }}
      />
      <span
        style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}
      >
        <span>LIVE</span>
        {time && (
          <span style={{ fontSize: 10, fontWeight: 600, color: "#64748b" }}>
            {time}
          </span>
        )}
      </span>
    </div>
    <div
      style={{
        width: 0,
        height: 0,
        borderLeft: "7px solid transparent",
        borderRight: "7px solid transparent",
        borderTop: "7px solid #3b82f6",
      }}
    />
  </div>
)

/* ================= STAT CARD ================= */

const StatCard = ({ icon, label, value, color }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: 12,
      padding: "12px 16px",
      display: "flex",
      alignItems: "center",
      gap: 12,
      border: `1px solid ${color}22`,
      boxShadow: `0 2px 8px ${color}12`,
    }}
  >
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: 10,
        background: `${color}18`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 18,
        color,
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div>
      <div
        style={{
          fontSize: 10,
          color: "#94a3b8",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 800,
          color: "#1e293b",
          lineHeight: 1.2,
        }}
      >
        {value}
      </div>
    </div>
  </div>
)

/* ================= PUNCH TIMELINE ================= */

const PunchTimeline = ({ attendance }) => {
  if (!attendance?.length) return null

  const entries = []
  attendance.forEach(item => {
    if (item.in_latitude)
      entries.push({
        type: "in",
        time: item.inTime,
        address: item.punchInAddress,
      })
    if (item.activePunch)
      entries.push({
        type: "live",
        time: item.outTime,
        address: item.punchOutAddress,
      })
    else if (item.outTime)
      entries.push({
        type: "out",
        time: item.outTime,
        address: item.punchOutAddress || null,
      })
  })

  const cfg = {
    in: { color: "#22c55e", icon: "✓", label: "Punch In" },
    live: { color: "#3b82f6", icon: "●", label: "LIVE" },
    out: { color: "#EF5350", icon: "✕", label: "Punch Out" },
  }

  return (
    <div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: "#1e293b",
          marginBottom: 16,
        }}
      >
        Punch Timeline
      </div>
      {entries.map((entry, i) => {
        const c = cfg[entry.type]
        return (
          <div key={i}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: c.color,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 900,
                  flexShrink: 0,
                }}
              >
                {c.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{ fontSize: 13, fontWeight: 700, color: c.color }}
                  >
                    {c.label}
                  </span>
                  <span
                    style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}
                  >
                    {entry.time || "—"}
                  </span>
                </div>
                {entry.address && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#94a3b8",
                      marginTop: 3,
                      lineHeight: 1.5,
                    }}
                  >
                    {entry.address}
                  </div>
                )}
              </div>
            </div>
            {i < entries.length - 1 && (
              <div
                style={{
                  marginLeft: 15,
                  marginTop: 4,
                  marginBottom: 4,
                  borderLeft: "2px dashed #e2e8f0",
                  height: 16,
                }}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

/* ================= LOCATION STAY TIMELINE ================= */

const STAY_COLORS = [
  "#1a73e8",
  "#34a853",
  "#fa7b17",
  "#ea4335",
  "#9334e6",
  "#00897b",
  "#e91e63",
  "#f09300",
]

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000
  const toRad = d => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

const toMins = t => {
  const [h, m] = t.split(":").map(Number)
  return h * 60 + m
}

function buildStayTimeline(coordinates, attendance, radiusMeters = 150) {
  if (!coordinates?.length) return []
  const sorted = [...coordinates].sort((a, b) => a.time.localeCompare(b.time))

  // Check if last attendance has no punch-out — if so, use last coordinate as implied end
  const lastAttendance = attendance?.[attendance.length - 1]
  const impliedEndTime =
    lastAttendance &&
    !lastAttendance.out_latitude &&
    !lastAttendance.activePunch
      ? sorted[sorted.length - 1].time.slice(0, 5)
      : null

  const groups = []
  let group = [sorted[0]]
  for (let i = 1; i < sorted.length; i++) {
    const anchor = group[0]
    const curr = sorted[i]
    if (
      haversineMeters(
        anchor.latitude,
        anchor.longitude,
        curr.latitude,
        curr.longitude
      ) <= radiusMeters
    ) {
      group.push(curr)
    } else {
      groups.push(group)
      group = [curr]
    }
  }
  groups.push(group)

  return groups.map((points, i) => {
    const startTime = points[0].time.slice(0, 5)
    const endTime = points[points.length - 1].time.slice(0, 5)
    const durationMins = Math.max(0, toMins(endTime) - toMins(startTime))
    const isLast = i === groups.length - 1
    return {
      locIndex: i + 1,
      startTime,
      endTime,
      durationMins,
      lat: points[0].latitude,
      lng: points[0].longitude,
      pointCount: points.length,
      impliedEnd: isLast && impliedEndTime ? impliedEndTime : null,
    }
  })
}

const fmtDur = mins => {
  if (mins < 1) return "< 1 min"
  if (mins < 60) return `${mins} min`
  const h = Math.floor(mins / 60),
    m = mins % 60
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

const fmt12 = t => {
  const [h, m] = t.split(":").map(Number)
  const ampm = h >= 12 ? "PM" : "AM"
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`
}

const LocationTimeline = ({ coordinates, attendance, onFlyTo }) => {
  const segments = buildStayTimeline(coordinates, attendance)
  if (!segments.length) return null

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 18,
        }}
      >
        <div
          style={{
            width: 4,
            height: 18,
            background: "#1a73e8",
            borderRadius: 2,
          }}
        />
        <span style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
          Location Timeline
        </span>
      </div>

      {segments.map((seg, i) => {
        const color = STAY_COLORS[(seg.locIndex - 1) % STAY_COLORS.length]
        const isLast = i === segments.length - 1
        const nextSeg = segments[i + 1]
        const transitMins = nextSeg
          ? Math.max(0, toMins(nextSeg.startTime) - toMins(seg.endTime))
          : 0
        const displayEnd = seg.impliedEnd || seg.endTime

        return (
          <div key={i}>
            {/* ── Stop card ── */}
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              {/* Pin column */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flexShrink: 0,
                  paddingTop: 2,
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: color,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 800,
                    boxShadow: `0 2px 8px ${color}55`,
                  }}
                >
                  {seg.locIndex}
                </div>
                {!isLast && (
                  <div
                    style={{
                      width: 2,
                      minHeight: 48,
                      background: "#e2e8f0",
                      margin: "4px 0",
                      borderRadius: 1,
                    }}
                  />
                )}
              </div>

              {/* Content */}
              <div style={{ flex: 1, paddingBottom: isLast ? 0 : 4 }}>
                <div
                  onClick={() => onFlyTo?.(seg.lat, seg.lng)}
                  style={{
                    cursor: "pointer",
                    background: "#fff",
                    borderRadius: 12,
                    padding: "10px 12px",
                    marginBottom: 2,
                    border: `1.5px solid ${color}30`,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    transition: "box-shadow 0.15s, border-color 0.15s",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.boxShadow = `0 3px 12px ${color}30`
                    e.currentTarget.style.borderColor = `${color}88`
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.boxShadow =
                      "0 1px 4px rgba(0,0,0,0.06)"
                    e.currentTarget.style.borderColor = `${color}30`
                  }}
                >
                  {/* Time range row */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#1e293b",
                      }}
                    >
                      {fmt12(seg.startTime)}
                      {seg.startTime !== displayEnd && (
                        <span style={{ color: "#1e293b", fontWeight: 700 }}>
                          {" "}
                          – {fmt12(displayEnd)}
                        </span>
                      )}
                      {seg.impliedEnd && (
                        <span
                          style={{
                            color: "#94a3b8",
                            fontSize: 10,
                            fontWeight: 400,
                          }}
                        >
                          {" "}
                          *
                        </span>
                      )}
                    </span>
                    {seg.durationMins > 0 ? (
                      <span
                        style={{
                          fontSize: 11,
                          background: `${color}15`,
                          color,
                          borderRadius: 20,
                          padding: "2px 8px",
                          fontWeight: 700,
                        }}
                      >
                        {seg.durationMins < 60
                          ? `${seg.durationMins} min stop`
                          : fmtDur(seg.durationMins)}
                      </span>
                    ) : (
                      <span
                        style={{
                          fontSize: 10,
                          color: "#94a3b8",
                          fontStyle: "italic",
                        }}
                      >
                        passing
                      </span>
                    )}
                  </div>
                  {/* Coordinates */}
                  <div
                    style={{
                      fontSize: 11,
                      color: "#64748b",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <span style={{ color, fontSize: 10 }}>📍</span>
                    {seg.lat.toFixed(5)}, {seg.lng.toFixed(5)}
                    <span style={{ color: "#cbd5e1", marginLeft: 4 }}>
                      · {seg.pointCount} pts
                    </span>
                  </div>
                </div>

                {/* ── Transit segment ── */}
                {!isLast && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 12px",
                    }}
                  >
                    <span style={{ fontSize: 13 }}>🚗</span>
                    <span
                      style={{
                        fontSize: 11,
                        color: "#64748b",
                        fontWeight: 500,
                      }}
                    >
                      In transit
                    </span>
                    {transitMins > 0 && (
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>
                        · {fmtDur(transitMins)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })}

      {/* Implied end note */}
      {segments.some(s => s.impliedEnd) && (
        <div
          style={{
            fontSize: 10,
            color: "#94a3b8",
            marginTop: 10,
            paddingLeft: 44,
          }}
        >
          * No punch-out recorded — last GPS point used as end time
        </div>
      )}
    </div>
  )
}

/* ================= LEGEND DOT ================= */

const LegendDot = ({ color, label, isLine, dark }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
    {isLine ? (
      <div
        style={{ width: 18, height: 3, background: color, borderRadius: 2 }}
      />
    ) : (
      <div
        style={{ width: 9, height: 9, borderRadius: "50%", background: color }}
      />
    )}
    <span
      style={{
        fontSize: 11,
        fontWeight: 500,
        color: dark ? "#cbd5e1" : "#64748b",
      }}
    >
      {label}
    </span>
  </div>
)

/* ================= MAIN COMPONENT ================= */

const LocationTracking = () => {
  const [employee, setEmployee] = useState("")
  const [date, setDate] = useState("")
  const [route, setRoute] = useState([])
  const [data, setData] = useState({})
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(false)
  const [popupInfo, setPopupInfo] = useState(null)
  const [viewState, setViewState] = useState({
    longitude: 72.8777,
    latitude: 19.076,
    zoom: 12,
  })
  const [mapStyle, setMapStyle] = useState(MAP_STYLES[0].value)
  const mapRef = useRef(null)

  const onMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (!map) return
    const size = 20
    const canvas = document.createElement("canvas")
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext("2d")
    ctx.fillStyle = "#ffffff"
    ctx.beginPath()
    ctx.moveTo(size / 2, 0)
    ctx.lineTo(size, size)
    ctx.lineTo(size / 2, size * 0.65)
    ctx.lineTo(0, size)
    ctx.closePath()
    ctx.fill()
    const { data } = ctx.getImageData(0, 0, size, size)
    map.addImage("route-arrow", { width: size, height: size, data })
  }, [])

  const { checkAccess } = usePermissionData()

  useEffect(() => {
    getEmployees()
  }, [])

  const getEmployees = async () => {
    const res = await fetchData(setLoading, "users")
    if (res?.success) setEmployees(res?.data)
  }

  const fetchLocationData = async () => {
    const res = await FetchPostData(
      setLoading,
      "attendance/user/route/by/date",
      { userId: employee, date }
    )
    if (res?.data?.success) {
      setData(res?.data)
      const pts = res?.data?.data?.polyline || []
      setRoute(pts)
      if (pts.length > 0) {
        setViewState(v => ({
          ...v,
          longitude: pts[0].lng,
          latitude: pts[0].lat,
          zoom: 13,
        }))
      }
    } else {
      Swal.fire("Error", res?.response?.data?.message, "error")
    }
  }

  const routePositions = route.map(p => [p.lng, p.lat])
  const hasRoute = routePositions.length > 0

  const geojsonRoute = {
    type: "Feature",
    geometry: { type: "LineString", coordinates: routePositions },
  }

  const routeLayer = {
    id: "route",
    type: "line",
    paint: {
      "line-color": "#6366f1",
      "line-width": 4,
      "line-opacity": 0.85,
    },
  }

  const arrowLayer = {
    id: "route-arrows",
    type: "symbol",
    layout: {
      "symbol-placement": "line",
      "symbol-spacing": 60,
      "icon-image": "route-arrow",
      "icon-size": 0.9,
      "icon-allow-overlap": true,
      "icon-rotation-alignment": "map",
    },
  }

  const totalTA = data?.expenses?.find(e => e?.type === "travel")?.value || 0
  const totalDA = data?.expenses?.find(e => e?.type === "dearness")?.value || 0

  const rawPunchMarkers =
    data?.attendance?.flatMap(item => {
      const arr = []
      if (item.in_latitude && item.in_longitude)
        arr.push({
          type: "in",
          position: [
            parseFloat(item.in_latitude),
            parseFloat(item.in_longitude),
          ],
          time: item.inTime,
          address: item.punchInAddress,
        })
      if (item.activePunch && item.out_latitude && item.out_longitude)
        arr.push({
          type: "live",
          position: [
            parseFloat(item.out_latitude),
            parseFloat(item.out_longitude),
          ],
          time: item.outTime,
          address: item.punchOutAddress,
        })
      else if (!item.activePunch && item.out_latitude && item.out_longitude)
        arr.push({
          type: "out",
          position: [
            parseFloat(item.out_latitude),
            parseFloat(item.out_longitude),
          ],
          time: item.outTime,
          address: item.punchOutAddress,
        })
      return arr
    }) || []

  const punchMarkers = spreadOverlappingMarkers(rawPunchMarkers)

  // If no punch-out recorded, use last coordinate as live position
  const lastAttendance = data?.attendance?.[data?.attendance?.length - 1]
  const hasNoPunchOut =
    lastAttendance &&
    !lastAttendance.out_latitude &&
    !lastAttendance.activePunch
  const liveFromCoord =
    hasNoPunchOut && data?.data?.coordinates?.length > 0
      ? [...data.data.coordinates]
          .sort((a, b) => a.time.localeCompare(b.time))
          .at(-1)
      : null

  const renderMarker = (m, i) => {
    const [lat, lng] = m.position
    return (
      <Marker
        key={i}
        latitude={lat}
        longitude={lng}
        anchor="bottom"
        onClick={e => {
          e.originalEvent.stopPropagation()
          setPopupInfo(m)
        }}
      >
        {m.type === "in" && (
          <CalloutMarker
            borderColor="#22c55e"
            bgColor="#22c55e"
            iconHtml="✓"
            labelText="Punch In"
            time={m.time}
          />
        )}
        {m.type === "out" && (
          <CalloutMarker
            borderColor="#EF5350"
            bgColor="#EF5350"
            iconHtml="✕"
            labelText="Punch Out"
            time={m.time}
          />
        )}
        {m.type === "live" && <LiveMarker time={m.time} />}
      </Marker>
    )
  }

  return (
    <PermittedComponent isAccess={checkAccess(7, "view")}>
      <div className="page-content">
        {loading && <Loader />}
        <Breadcrumb
          title="Employee Tracking"
          breadcrumbItem="Location Tracking"
        />

        {hasRoute ? (
          <Row className="g-3" style={{ alignItems: "stretch" }}>
            {/* ── MAP ── */}
            <Col md={8} style={{ display: "flex", flexDirection: "column" }}>
              <Card
                style={{
                  borderRadius: 16,
                  border: "none",
                  boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
                  overflow: "hidden",
                  flex: 1,
                }}
              >
                {/* Dark header with controls */}
                <div
                  style={{
                    padding: "12px 20px",
                    background: "linear-gradient(135deg, #1e293b, #334155)",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <MdLocationOn
                    size={17}
                    color="#6366f1"
                    style={{ flexShrink: 0 }}
                  />
                  <Input
                    type="select"
                    value={employee}
                    onChange={e => setEmployee(e.target.value)}
                    style={{
                      width: 170,
                      height: 34,
                      fontSize: 12,
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.15)",
                      background: "rgba(255,255,255,0.1)",
                      color: "#fff",
                      paddingLeft: 10,
                    }}
                  >
                    <option value="" style={{ color: "#1e293b" }}>
                      Select Employee
                    </option>
                    {employees.map(emp => (
                      <option
                        key={emp.id}
                        value={emp.id}
                        style={{ color: "#1e293b" }}
                      >
                        {emp.name}
                      </option>
                    ))}
                  </Input>
                  <Input
                    type="date"
                    value={date}
                    onChange={e => setDate(e.target.value)}
                    style={{
                      width: 148,
                      height: 34,
                      fontSize: 12,
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.15)",
                      background: "rgba(255,255,255,0.1)",
                      color: "#fff",
                      paddingLeft: 10,
                      colorScheme: "dark",
                    }}
                  />
                  <Button
                    disabled={!employee || !date}
                    onClick={fetchLocationData}
                    className="btn-blue"
                    style={{
                      height: 34,
                      fontSize: 12,
                      paddingLeft: 14,
                      paddingRight: 14,
                    }}
                  >
                    <FaRoute size={12} style={{ marginRight: 6 }} />
                    Track Route
                  </Button>
                  <Input
                    type="select"
                    value={mapStyle}
                    onChange={e => setMapStyle(e.target.value)}
                    style={{
                      width: 160,
                      height: 34,
                      fontSize: 12,
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.15)",
                      background: "rgba(255,255,255,0.1)",
                      color: "#fff",
                      paddingLeft: 10,
                    }}
                  >
                    {MAP_STYLES.map(s => (
                      <option
                        key={s.value}
                        value={s.value}
                        style={{ color: "#1e293b" }}
                      >
                        {s.label}
                      </option>
                    ))}
                  </Input>
                  <div
                    style={{
                      marginLeft: "auto",
                      display: "flex",
                      gap: 14,
                      alignItems: "center",
                    }}
                  >
                    <LegendDot color="#22c55e" label="Start" dark />
                    <LegendDot color="#EF5350" label="End" dark />
                    <LegendDot color="#6366f1" label="Route" dark isLine />
                  </div>
                </div>

                {/* Mapbox Map */}
                <div style={{ height: 560 }}>
                  <Map
                    {...viewState}
                    ref={mapRef}
                    onMove={e => setViewState(e.viewState)}
                    onLoad={onMapLoad}
                    mapboxAccessToken={MAPBOX_TOKEN}
                    mapStyle={mapStyle}
                    style={{ width: "100%", height: "100%" }}
                  >
                    <NavigationControl position="top-right" />
                    <FullscreenControl position="top-right" />
                    <ScaleControl />

                    <Source id="route" type="geojson" data={geojsonRoute}>
                      <Layer {...routeLayer} />
                      {/* <Layer {...arrowLayer} /> */}
                    </Source>

                    {punchMarkers.map(renderMarker)}
                    {liveFromCoord && (
                      <Marker
                        latitude={parseFloat(liveFromCoord.latitude)}
                        longitude={parseFloat(liveFromCoord.longitude)}
                        anchor="bottom"
                        onClick={e => {
                          e.originalEvent.stopPropagation()
                          setPopupInfo({
                            position: [
                              parseFloat(liveFromCoord.latitude),
                              parseFloat(liveFromCoord.longitude),
                            ],
                            time: liveFromCoord.time.slice(0, 5),
                            address: "Last known location",
                          })
                        }}
                      >
                        <LiveMarker time={liveFromCoord.time.slice(0, 5)} />
                      </Marker>
                    )}

                    {popupInfo && (
                      <Popup
                        latitude={popupInfo.position[0]}
                        longitude={popupInfo.position[1]}
                        anchor="top"
                        onClose={() => setPopupInfo(null)}
                        closeOnClick={false}
                      >
                        <div style={{ fontSize: 11, maxWidth: 220 }}>
                          <b style={{ fontSize: 12 }}>
                            {popupInfo.time || "—"}
                          </b>
                          <br />
                          {popupInfo.address || "No address"}
                          {popupInfo._overlapped && (
                            <div
                              style={{
                                marginTop: 4,
                                color: "#94a3b8",
                                fontSize: 10,
                              }}
                            >
                              ℹ️ Marker slightly offset — same location as
                              another punch
                            </div>
                          )}
                        </div>
                      </Popup>
                    )}
                  </Map>
                </div>

                {/* Bottom legend */}
                <div
                  style={{
                    padding: "10px 20px",
                    background: "#f8fafc",
                    display: "flex",
                    gap: 20,
                    justifyContent: "center",
                    borderTop: "1px solid #e2e8f0",
                  }}
                >
                  <LegendDot color="#22c55e" label="Punch In" />
                  <LegendDot color="#3b82f6" label="Live Location" />
                  <LegendDot color="#EF5350" label="Punch Out" />
                </div>
              </Card>
            </Col>

            {/* ── RIGHT PANEL ── */}
            <Col md={4} style={{ display: "flex", flexDirection: "column" }}>
              <div
                style={{
                  maxHeight: 660,
                  overflowY: "auto",
                  scrollbarWidth: "thin",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                  paddingRight: 2,
                }}
              >
                <Card
                  className="mb-3"
                  style={{
                    borderRadius: 16,
                    border: "none",
                    boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
                  }}
                >
                  <CardBody style={{ padding: 20 }}>
                    <div
                      style={{ display: "flex", flexDirection: "row", gap: 10 }}
                    >
                      <StatCard
                        icon={<FaRoad />}
                        label="Distance"
                        value={`${data?.data?.distance?.km ?? "—"} km`}
                        color="#6366f1"
                      />
                      <StatCard
                        icon={<FaMoneyBillWave />}
                        label="TA"
                        value={`₹${totalTA}`}
                        color="#22c55e"
                      />
                      <StatCard
                        icon={<FaMoneyBillWave />}
                        label="DA"
                        value={`₹${totalDA}`}
                        color="#f59e0b"
                      />
                    </div>
                  </CardBody>
                </Card>
                <Card
                  className="mb-3"
                  style={{
                    borderRadius: 16,
                    border: "none",
                    boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
                  }}
                >
                  <CardBody style={{ padding: 20 }}>
                    <PunchTimeline attendance={data?.attendance} />
                  </CardBody>
                </Card>
                <Card
                  style={{
                    borderRadius: 16,
                    border: "none",
                    boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
                  }}
                >
                  <CardBody style={{ padding: 20 }}>
                    <LocationTimeline
                      coordinates={data?.data?.coordinates}
                      attendance={data?.attendance}
                      onFlyTo={(lat, lng) =>
                        setViewState(v => ({
                          ...v,
                          latitude: lat,
                          longitude: lng,
                          zoom: 16,
                        }))
                      }
                    />
                  </CardBody>
                </Card>
              </div>
            </Col>
          </Row>
        ) : (
          /* ── EMPTY STATE ── */
          <>
            <Card
              style={{
                borderRadius: 16,
                border: "none",
                boxShadow: "0 2px 16px rgba(99,102,241,0.08)",
                marginBottom: 20,
              }}
            >
              <CardBody style={{ padding: "18px 24px" }}>
                <Row className="g-3 align-items-end">
                  <Col md={3}>
                    <Input
                      type="select"
                      className="form-select rounded-4"
                      onChange={e => setEmployee(e.target.value)}
                    >
                      <option value="">Select Employee</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={3}>
                    <Input
                      type="date"
                      className="rounded-4"
                      onChange={e => setDate(e.target.value)}
                    />
                  </Col>
                  <Col md={2}>
                    <Button
                      disabled={!employee || !date}
                      onClick={fetchLocationData}
                      className="btn-blue"
                    >
                      <FaRoute size={14} style={{ marginRight: 6 }} />
                      Track Route
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card
              style={{
                borderRadius: 16,
                border: "none",
                boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
              }}
            >
              <CardBody>
                <div
                  style={{
                    textAlign: "center",
                    padding: "60px 20px",
                    color: "#94a3b8",
                  }}
                >
                  <FaRoute
                    size={48}
                    style={{ marginBottom: 16, opacity: 0.3 }}
                  />
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#64748b",
                      marginBottom: 6,
                    }}
                  >
                    No Route Found
                  </div>
                  <div style={{ fontSize: 13 }}>
                    Select an employee and date, then click Track Route.
                  </div>
                </div>
              </CardBody>
            </Card>
          </>
        )}
      </div>
    </PermittedComponent>
  )
}

export default LocationTracking
